/**
 * OTP send + verify — server-side. Calls the `sendOtp` and `verifyOtp` Cloud
 * Functions in `functions/auth/otp.js`. The code never leaves the server.
 *
 * The legacy client-side OTP (generated in browser, stored in memory) is gone
 * — no more "open DevTools and read the code" attack.
 */

import { getFirebase } from '../../core/firebase.js';
import { Firebase } from '../../app/config.js';
import { normalize as normalizePhone } from '../../shared/phone.js';
import { createLogger } from '../../core/logger.js';
import { getDeviceId } from './device.service.js';

const log = createLogger('otp');

/** @type {string | null} */
let pendingPhone = null;

/**
 * Send a fresh OTP to the given phone via the Cloud Function.
 *
 * @param {string} phoneInput
 * @returns {Promise<{ sent: boolean; phone: string }>}
 */
export async function send(phoneInput) {
  const phone = normalizePhone(phoneInput);
  if (!phone) throw new Error('OTP: invalid phone');

  try {
    const functions = getFirebase().app().functions(Firebase.functionsRegion);
    await functions.httpsCallable('sendOtp')({ phone });
    pendingPhone = phone;
    log.info('OTP requested for', phone);
    return { sent: true, phone };
  } catch (err) {
    pendingPhone = null;
    const e = /** @type {{ code?: string; message?: string }} */ (err);
    log.warn('sendOtp failed:', e);
    throw new Error(e.message || 'לא הצלחנו לשלוח SMS. נסה שוב.');
  }
}

/**
 * Verify a code against the pending OTP via the Cloud Function.
 *
 * Returns a discriminated union to match the legacy callsite contract — the
 * UI in `otp.view.js` shows `result.reason` on failure.
 *
 * @param {string} code
 * @returns {Promise<{ valid: true } | { valid: false; reason: string }>}
 */
export async function verify(code) {
  if (!pendingPhone) return { valid: false, reason: 'לא נשלח קוד אימות' };
  const trimmed = String(code).trim();
  if (!/^\d{4,8}$/.test(trimmed)) {
    return { valid: false, reason: 'הזן קוד מספרי תקין' };
  }

  try {
    const functions = getFirebase().app().functions(Firebase.functionsRegion);
    const result = await functions.httpsCallable('verifyOtp')({
      phone: pendingPhone,
      code: trimmed,
      // Server-side trust enrollment — on success the server writes
      // `trustedDevices/{phone}/{deviceId}` so future logins from the same
      // device can skip OTP.
      deviceId: getDeviceId(),
    });
    if (result?.data?.verified) {
      const phone = pendingPhone;
      pendingPhone = null;
      log.info('OTP verified for', phone);
      return { valid: true };
    }
    return { valid: false, reason: 'קוד שגוי' };
  } catch (err) {
    const e = /** @type {{ code?: string; message?: string }} */ (err);
    log.warn('verifyOtp failed:', e);
    return { valid: false, reason: e.message || 'אימות נכשל' };
  }
}

/** Clear pending phone tracker (e.g. when the user cancels the modal). */
export function reset() {
  pendingPhone = null;
}

/** @returns {boolean} */
export function isPending() {
  return pendingPhone != null;
}
